/* eslint-disable camelcase */

import { push } from 'redux-first-history';

import { AppDispatch } from 'types';
import { sessionExpired } from 'containers/App/utils/expirationTimer';

export const hasValidToken = (): boolean => {
  if (!localStorage.getItem('phoenixAuthToken')) {
    return false;
  }

  try {
    return !sessionExpired();
  } catch {
    return false;
  }
};

type InitialAuthState = {
  isFetching: boolean;
  authenticated: boolean;
  permissions: boolean;
  companyType: boolean;
  membershipRole: null;
  adminMembershipId: null;
  bullhorn: null;
  mfaInProgress: boolean;
};

export const getInitialAuthState = (): InitialAuthState => {
  const hasToken = hasValidToken();

  return {
    isFetching: hasToken,
    authenticated: hasToken,
    permissions: false,
    companyType: false,
    membershipRole: null,
    adminMembershipId: null,
    bullhorn: null,
    mfaInProgress: false,
  };
};

/**
 * Clear authentication tokens and redirect to login.
 * Uses window.location.href for a full page reload to clear all Redux state,
 * which is necessary when already on /login (e.g., MFA challenge screen).
 */
export const clearAuthAndRedirect = (reason?: string) => {
  localStorage.removeItem('phoenixAuthToken');
  localStorage.removeItem('phoenixExpToken');
  sessionStorage.removeItem('mfaRedirectPath');

  const target = reason ? `/login?reason=${reason}` : '/login';
  window.location.href = target;
};

/**
 * Resolves the post-login redirect path based on priority order.
 * Returns the path to navigate to, or null if a full-page redirect was triggered.
 */
function resolveRedirectPath(
  user: CurrentUser,
  company?: CurrentCompany,
  redirectPath?: string,
  isAdminImpersonation: boolean = false
): string | null {
  // Priority 1: Simple user redirect
  const simpleRedirect = getAndClearSessionKey('simpleUserRedirectPath');

  if (simpleRedirect) return simpleRedirect;

  // Priority 2: Email confirmation redirect
  const emailConfirmRedirect = getAndClearSessionKey(
    'emailConfirmRedirectPath'
  );
  if (emailConfirmRedirect) return emailConfirmRedirect;

  // Priority 3: User needs onboarding
  if (user.needs_onboarding) return '/onboarding';

  // Priority 4: User needs password reset
  if (user.needs_password) return '/resetpassword';

  // Priority 5: Administrator redirect
  if (company?.company_type === 'administrator' && !isAdminImpersonation) {
    return '/admin';
  }

  // Priority 6: Bullhorn SSO redirect
  const bullhornRedirect = getAndClearSessionKey('bullhornSSORedirectPath');
  if (bullhornRedirect) return bullhornRedirect;

  // Priority 7: Reset password path with token
  const passwordResetToken = getAndClearSessionKey('passwordResetToken');
  if (
    redirectPath &&
    redirectPath.startsWith('/resetpassword') &&
    passwordResetToken
  ) {
    return `/resetpassword?password_reset_token=${passwordResetToken}`;
  }

  // Priority 8: Explicit redirect path provided
  if (redirectPath) return redirectPath;

  // Priority 9: Default redirect
  return '/';
}

type PostLoginRedirectParams = {
  dispatch: AppDispatch;
  user: CurrentUser;
  company?: CurrentCompany;
  redirectPath?: string;
  isAdminImpersonation?: boolean;
};

export function handlePostLoginRedirect(params: PostLoginRedirectParams): void {
  const {
    dispatch,
    user,
    company,
    redirectPath,
    isAdminImpersonation = false,
  } = params;

  const path = resolveRedirectPath(
    user,
    company,
    redirectPath,
    isAdminImpersonation
  );

  if (path) {
    dispatch(push(path));
  }
}

export function getAndClearSessionKey(key: string): string | null {
  const value = sessionStorage.getItem(key);
  if (value) {
    sessionStorage.removeItem(key);
  }
  return value;
}

type LoginResponse = {
  mfa_required?: boolean;
  mfa_setup_required?: boolean;
  session_token?: string;
  method?: string;
  extra_data?: {
    mfa_setup_required?: boolean;
    trusted_device_token?: string;
  };
};

export function isMFARequired(response: LoginResponse): boolean {
  return response.mfa_required === true;
}

export function isMFASetupRequired(response: LoginResponse): boolean {
  return response.extra_data?.mfa_setup_required === true;
}

type MFARequiredError = {
  mfa_required: true;
  session_token: string | undefined;
  method: string | undefined;
};

export function createMFARequiredError(
  response: LoginResponse
): MFARequiredError {
  return {
    mfa_required: true,
    session_token: response.session_token,
    method: response.method,
  };
}
